import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Recommendations = ({ user, onLogout }) => {
  const [recommendations, setRecommendations] = useState([]);
  const [riskProfiles, setRiskProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    product_name: '',
    product_type: 'Equity',
    isin: '',
    recommendation_type: 'Buy',
    target_allocation: 0,
    risk_profile_ids: [],
    rationale: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recsRes, profilesRes] = await Promise.all([
        axios.get(`${API}/recommendations`),
        axios.get(`${API}/risk-profiles`),
      ]);
      setRecommendations(recsRes.data);
      setRiskProfiles(profilesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/recommendations`, formData);
      toast.success('Recommendation created successfully');
      setDialogOpen(false);
      fetchData();
      setFormData({
        product_name: '',
        product_type: 'Equity',
        isin: '',
        recommendation_type: 'Buy',
        target_allocation: 0,
        risk_profile_ids: [],
        rationale: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create recommendation');
    }
  };

  const getRecommendationIcon = (type) => {
    switch (type) {
      case 'Buy':
        return <TrendingUp className="w-5 h-5 text-green-400" />;
      case 'Sell':
        return <TrendingDown className="w-5 h-5 text-red-400" />;
      default:
        return <Minus className="w-5 h-5 text-yellow-400" />;
    }
  };

  const getRecommendationColor = (type) => {
    switch (type) {
      case 'Buy':
        return 'from-green-500/20 to-emerald-500/20 border-green-500/30';
      case 'Sell':
        return 'from-red-500/20 to-rose-500/20 border-red-500/30';
      default:
        return 'from-yellow-500/20 to-amber-500/20 border-yellow-500/30';
    }
  };

  const groupedRecommendations = recommendations.reduce((acc, rec) => {
    if (!acc[rec.product_type]) acc[rec.product_type] = [];
    acc[rec.product_type].push(rec);
    return acc;
  }, {});

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="recommendations-container">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
              Central Recommendations
            </h1>
            <p className="text-gray-600">P&S Desk product recommendations repository</p>
          </div>
          {['P&S_Desk', 'Product_Lead', 'Admin'].includes(user?.role) && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black" data-testid="create-recommendation-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Recommendation
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Add Product Recommendation</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="product_name" className="text-gray-700">Product Name *</Label>
                      <Input
                        id="product_name"
                        value={formData.product_name}
                        onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                        required
                        className="bg-white border-gray-300 text-gray-900"
                        data-testid="product-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="product_type" className="text-gray-700">Product Type *</Label>
                      <select
                        id="product_type"
                        value={formData.product_type}
                        onChange={(e) => setFormData({ ...formData, product_type: e.target.value })}
                        className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                        data-testid="product-type-select"
                      >
                        <option value="Equity">Equity</option>
                        <option value="Debt">Debt</option>
                        <option value="Mutual Fund">Mutual Fund</option>
                        <option value="ETF">ETF</option>
                        <option value="Alternative">Alternative</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="isin">ISIN</Label>
                      <Input
                        id="isin"
                        value={formData.isin}
                        onChange={(e) => setFormData({ ...formData, isin: e.target.value })}
                        className="bg-slate-800 border-slate-700"
                        data-testid="isin-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="recommendation_type">Recommendation *</Label>
                      <select
                        id="recommendation_type"
                        value={formData.recommendation_type}
                        onChange={(e) => setFormData({ ...formData, recommendation_type: e.target.value })}
                        className="w-full px-3 py-2 rounded-md bg-slate-800 border border-slate-700"
                        data-testid="recommendation-type-select"
                      >
                        <option value="Buy">Buy</option>
                        <option value="Hold">Hold</option>
                        <option value="Sell">Sell</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="target_allocation">Target Allocation (%) *</Label>
                      <Input
                        id="target_allocation"
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={formData.target_allocation}
                        onChange={(e) => setFormData({ ...formData, target_allocation: parseFloat(e.target.value) })}
                        required
                        className="bg-slate-800 border-slate-700"
                        data-testid="target-allocation-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Applicable Risk Profiles *</Label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {riskProfiles.map((profile) => (
                          <label key={profile.id} className="flex items-center gap-2 p-3 bg-slate-800 rounded cursor-pointer hover:bg-slate-700">
                            <input
                              type="checkbox"
                              checked={formData.risk_profile_ids.includes(profile.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({ ...formData, risk_profile_ids: [...formData.risk_profile_ids, profile.id] });
                                } else {
                                  setFormData({ ...formData, risk_profile_ids: formData.risk_profile_ids.filter((id) => id !== profile.id) });
                                }
                              }}
                              className="rounded"
                            />
                            <span className="text-sm text-white">{profile.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="rationale">Rationale *</Label>
                      <Textarea
                        id="rationale"
                        value={formData.rationale}
                        onChange={(e) => setFormData({ ...formData, rationale: e.target.value })}
                        required
                        className="bg-slate-800 border-slate-700"
                        rows={4}
                        data-testid="rationale-input"
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full btn-primary" data-testid="submit-recommendation-button">
                    Add Recommendation
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <Tabs defaultValue="all" className="w-full">
          <TabsList>
            <TabsTrigger value="all" data-testid="all-recommendations-tab">All ({recommendations.length})</TabsTrigger>
            {Object.keys(groupedRecommendations).map((type) => (
              <TabsTrigger key={type} value={type} data-testid={`${type.toLowerCase()}-tab`}>
                {type} ({groupedRecommendations[type].length})
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="all" className="mt-6">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...Array(4)].map((_, i) => <div key={i} className="loading-shimmer h-40 rounded-xl" />)}
              </div>
            ) : recommendations.length === 0 ? (
              <Card className="border-slate-700 bg-slate-900/80">
                <CardContent className="p-12 text-center">
                  <TrendingUp className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                  <h3 className="text-white text-lg font-semibold mb-2">No Recommendations</h3>
                  <p className="text-slate-400">Add your first recommendation to get started</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {recommendations.map((rec) => (
                  <Card key={rec.id} className={`border bg-gradient-to-br ${getRecommendationColor(rec.recommendation_type)} backdrop-blur-xl card-hover`} data-testid={`recommendation-card-${rec.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-white text-lg">{rec.product_name}</CardTitle>
                          <p className="text-slate-400 text-sm mt-1">{rec.product_type}</p>
                        </div>
                        {getRecommendationIcon(rec.recommendation_type)}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded">
                        <span className="text-slate-400 text-sm">Target Allocation</span>
                        <span className="text-white font-semibold">{rec.target_allocation}%</span>
                      </div>
                      <div className="p-3 bg-slate-900/50 rounded">
                        <p className="text-slate-400 text-xs mb-1">Rationale</p>
                        <p className="text-white text-sm">{rec.rationale}</p>
                      </div>
                      {rec.isin && (
                        <div className="text-xs text-slate-500">
                          ISIN: {rec.isin}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {Object.keys(groupedRecommendations).map((type) => (
            <TabsContent key={type} value={type} className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {groupedRecommendations[type].map((rec) => (
                  <Card key={rec.id} className={`border bg-gradient-to-br ${getRecommendationColor(rec.recommendation_type)} backdrop-blur-xl card-hover`} data-testid={`recommendation-card-${rec.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-white text-lg">{rec.product_name}</CardTitle>
                          <p className="text-slate-400 text-sm mt-1">{rec.product_type}</p>
                        </div>
                        {getRecommendationIcon(rec.recommendation_type)}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded">
                        <span className="text-slate-400 text-sm">Target Allocation</span>
                        <span className="text-white font-semibold">{rec.target_allocation}%</span>
                      </div>
                      <div className="p-3 bg-slate-900/50 rounded">
                        <p className="text-slate-400 text-xs mb-1">Rationale</p>
                        <p className="text-white text-sm">{rec.rationale}</p>
                      </div>
                      {rec.isin && (
                        <div className="text-xs text-slate-500">
                          ISIN: {rec.isin}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </Layout>
  );
};

export default Recommendations;
